import * as React from 'react';

export const STUDENTS = 'students'
export const ASSIGNED_ON = 'assignedOn'
export const LAST_ACTIVE = 'lastActive'
export const LEARNING_TOOLS = 'learningTools'

export type CardPart = typeof STUDENTS | typeof ASSIGNED_ON | typeof LAST_ACTIVE | typeof LEARNING_TOOLS

export const CARD_PARTS: Array<{ label: string, value: CardPart }> = [
  { label: 'Students', value: STUDENTS },
  { label: 'Assigned on', value: ASSIGNED_ON },
  { label: 'Last active', value: LAST_ACTIVE },
  { label: 'Learning tool', value: LEARNING_TOOLS },
]

export const ALL_CARD_PARTS: CardPart[] = CARD_PARTS.map(part => part.value)

const COMPACT = 'compact'
const DETAILED = 'detailed'

type View = typeof COMPACT | typeof DETAILED

const HIDDEN_PARTS_STORAGE_KEY = 'manageActivitiesV2HiddenCardParts'

const DEFAULT_HIDDEN_PARTS: Record<View, CardPart[]> = { [COMPACT]: [LAST_ACTIVE, LEARNING_TOOLS], [DETAILED]: [] }

const storageKey = (view: View) => `${HIDDEN_PARTS_STORAGE_KEY}-${view}`

// saves what is hidden rather than shown, so a part added later appears for everyone by default
function storedHiddenParts(view: View): CardPart[] {
  try {
    const stored = JSON.parse(window.localStorage.getItem(storageKey(view)) || 'null')
    return Array.isArray(stored) ? ALL_CARD_PARTS.filter(part => stored.includes(part)) : DEFAULT_HIDDEN_PARTS[view]
  } catch {
    return DEFAULT_HIDDEN_PARTS[view]
  }
}

export function useShownCardParts(isCompact: boolean): [CardPart[], (parts: CardPart[]) => void] {
  const view: View = isCompact ? COMPACT : DETAILED
  const [hiddenByView, setHiddenByView] = React.useState(() => ({ [COMPACT]: storedHiddenParts(COMPACT), [DETAILED]: storedHiddenParts(DETAILED) }))

  const setShownParts = React.useCallback((parts: CardPart[]) => {
    const hidden = ALL_CARD_PARTS.filter(part => !parts.includes(part))
    try {
      window.localStorage.setItem(storageKey(view), JSON.stringify(hidden))
    } catch {
      // the page still updates when the browser will not save, e.g. a full or blocked storage
    }
    setHiddenByView(previous => ({ ...previous, [view]: hidden }))
  }, [view])

  const shownParts = React.useMemo(() => ALL_CARD_PARTS.filter(part => !hiddenByView[view].includes(part)), [hiddenByView, view])

  return [shownParts, setShownParts]
}
